import React, { FC, useState, useDeferredValue, useMemo, useEffect } from 'react';
import { Slider } from 'antd';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import './ActivityLevelStep.scss';

interface ActivityLevelStepProps {
  clientName: string;
  value?: number;
  onChange?: (val: number) => void;
}

// What's {clientName}'s non-gym activity like?
export const ActivityLevelStep: FC<ActivityLevelStepProps> = ({ clientName, value, onChange }) => {
  const { data: programMasterData, isLoading, isError } = useGetProgramMasterData();
  const nonGymData = programMasterData?.nonGymActivity;

  // Use dynamic slider boundaries from API, fallback to sensible defaults
  const min = nonGymData?.slider?.min;
  const max = nonGymData?.slider?.max;
  const step = nonGymData?.slider?.step;
  const title = nonGymData?.title?.replace('{{clientName}}', clientName);
  const subtitle = nonGymData?.subtitle || '';

  const [localActivityLike, setLocalActivityLike] = useState<number>(min ?? 0);
  const activityLike = value !== undefined ? value : localActivityLike;

  useEffect(() => {
    if (value === undefined && onChange) {
      onChange(min ?? 0);
    }
  }, [value, onChange, min]);

  const deferredActivityLike = useDeferredValue(activityLike);
  const options = nonGymData?.options;

  const currentDetails = useMemo(() => {
    // Default fallback to first option
    let matchedOption = options?.[0];

    // Find the option where percentage falls between minValue and maxValue
    if (options) {
      for (const option of options) {
        const optionMin = option.minValue ?? 0;
        const optionMax = option.maxValue ?? 100;
        if (deferredActivityLike >= optionMin && deferredActivityLike <= optionMax) {
          matchedOption = option;
          break;
        }
      }
    }

    return {
      percent: `${deferredActivityLike}% ACTIVE`,
      title: matchedOption?.title,
      description: matchedOption?.subtitle,
    };
  }, [deferredActivityLike, options]);

  if (isLoading) {
    return <div className="activity-level-step">{'Loading options...'}</div>;
  }

  if (isError || !nonGymData) {
    return <div className="activity-level-step">{'Error loading options.'}</div>;
  }

  return (
    <div className="activity-level-step">
      <h2 className="step-title">{title}</h2>
      {subtitle && <p className="step-subtitle">{subtitle}</p>}

      <div className="activity-card">
        <div className="slider-container">
          <Slider
            min={min}
            max={max}
            step={step}
            defaultValue={min}
            value={activityLike}
            onChange={(val) => {
              if (onChange) onChange(val);
              setLocalActivityLike(val);
            }}
            tooltip={{ open: false }}
            className="activity-slider"
          />
        </div>

        <div className="activity-content">
          <span className="percent-active">{currentDetails.percent}</span>
          <h3 className="activity-title">{currentDetails.title}</h3>
          <p className="activity-description">{currentDetails.description}</p>
        </div>
      </div>
    </div>
  );
};
